import React, { useState, useEffect, useRef, useCallback } from 'react';
import airportService from '../../services/airportService';

const AirportSelect = ({
  value,
  onChange,
  placeholder = "Select Airport",
  className = "",
  error = "",
  icon = "fas fa-plane",
  onClearError
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasPrev, setHasPrev] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownSearchRef = useRef(null);

  // Load initial airports when component mounts
  useEffect(() => {
    if (airports.length === 0) {
      loadAirports(1, '');
    }
  }, []); // Empty dependency array - only run once on mount

  // Load airports based on search term
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm === '') {
        setPage(1);
        setAirports([]);
        setHasMore(true);
        loadAirports(1, searchTerm);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && dropdownSearchRef.current) {
      setTimeout(() => {
        dropdownSearchRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  // Find selected airport when value changes
  useEffect(() => {
    if (value) {
      const airport = airports.find(a => a.id.toString() === value.toString());
      if (airport) {
        setSelectedAirport(airport);
      }
    } else {
      setSelectedAirport(null);
    }
  }, [value, airports]);

  // Intersection Observer for infinite scroll - DISABLED to prevent auto-triggering
  // Users can use the "Load More" button or Next/Previous buttons instead
  useEffect(() => {
    // Intersection observer disabled - only manual pagination
    return () => { };
  }, []);

  const loadAirports = async (pageNum = 1, search = '', replace = true) => {
    try {
      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await airportService.getAirportList(search, pageNum);

      // Handle the new response format with pagination metadata
      const airportData = response.data || [];
      const paginationInfo = response.pagination || {};

      if (replace) {
        setAirports(airportData);
      } else {
        setAirports(prev => [...prev, ...airportData]);
      }

      // Update pagination state
      setHasMore(paginationInfo.hasMore || false);
      setHasPrev(pageNum > 1);
      setTotalPages(paginationInfo.totalPages || 0);
      setTotalCount(paginationInfo.total || airportData.length);
      setPage(pageNum);
    } catch (error) {
      if (replace) {
        setAirports([]);
      }
      setHasMore(false);
      setHasPrev(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && airports.length > 0) {
      const nextPage = page + 1;
      loadAirports(nextPage, searchTerm, false);
    }
  }, [page, searchTerm, loadingMore, hasMore, airports.length]);

  const handleNextPage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasMore && !loading && !loadingMore) {
      const nextPage = page + 1;
      loadAirports(nextPage, searchTerm, true);
    }
  };

  const handlePrevPage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasPrev && !loading && !loadingMore) {
      const prevPage = page - 1;
      loadAirports(prevPage, searchTerm, true);
    }
  };

  const handleLoadMoreClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!loadingMore && hasMore && airports.length > 0) {
      loadMore();
    }
  };

  const handleInputClick = () => {
    setIsOpen(true);
    if (airports.length === 0) {
      loadAirports(1, '');
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);

    // Clear error when user starts typing
    if (error && onClearError) {
      onClearError();
    }
  };

  const handleAirportSelect = (airport) => {
    setSelectedAirport(airport);
    setSearchTerm('');
    setIsOpen(false);
    onChange(airport.id, airport);

    // Clear error when selection is made
    if (error && onClearError) {
      onClearError();
    }
  };

  const getDisplayValue = () => {
    if (selectedAirport) {
      return `${selectedAirport.airport_name} (${selectedAirport.iata})`;
    }
    return searchTerm;
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative flex items-center w-full">
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
          {typeof icon === 'string' && icon.startsWith('fa') ? (
            <i className={icon}></i>
          ) : (
            <img 
              src={icon} 
              alt="icon" 
              style={{
                cursor: 'pointer',
                lineHeight: '33px',
                textAlign: 'right',
                width: '35px',
                color: '#312d65',
                zIndex: 9
              }}
            />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          className={`w-full h-14 pl-16 pr-12 rounded-lg text-sm text-jet-gray-700 bg-white transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] placeholder-jet-gray-400 cursor-pointer ${className} ${error ? 'border-red-500' : 'border-jet-gray-300'}`}
          placeholder={placeholder}
          value={getDisplayValue()}
          readOnly
          onClick={handleInputClick}
          autoComplete="off"
        />
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <i className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-jet-gray-400 text-sm`}></i>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-jet-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto mt-1">
          {/* Dropdown Search Input */}
          <div className="sticky top-0 z-[60] bg-white p-2 border-b border-jet-gray-200 shadow-sm">
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-jet-gray-400">
                {/* <i className="fas fa-search"></i> */}
              </div>
              <input
                ref={dropdownSearchRef}
                type="text"
                className="w-full h-10 pl-10 pr-4 border border-jet-gray-300 rounded-lg focus:border-jet-yellow focus:outline-none text-sm bg-jet-gray-50 transition-all duration-200"
                placeholder="Search for airport..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {loading ? (
            <div className="p-4 text-center text-jet-gray-500">
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Loading airports...
            </div>
          ) : (
            <>
              {airports.length > 0 ? (
                <>
                  {/* Airport List */}
                  {airports.map((airport) => (
                    <div
                      key={airport.id}
                      className="p-3 hover:bg-jet-gray-50 cursor-pointer border-b border-jet-gray-100 transition-colors duration-150"
                      onClick={() => handleAirportSelect(airport)}
                    >
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium text-jet-gray-800">
                          {`${airport.airport_name} (${airport.iata}) ${airport.country}`}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Load More Section */}
                  {hasMore && (
                    <div className="sticky bottom-0 bg-white border-t border-jet-gray-200">
                      {/* Manual Load More Button */}
                      <div className="p-3">
                        <button
                          type="button"
                          onClick={handleLoadMoreClick}
                          disabled={loadingMore || !hasMore}
                          className="w-full py-2 px-4 bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {loadingMore ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              <span>Loading more airports...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-chevron-down"></i>
                              <span>Load More Airports</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Results Counter */}
                      <div className="px-3 pb-2 text-center text-xs text-jet-gray-500">
                        Showing {airports.length} airports
                      </div>
                    </div>
                  )}




                  {/* End of Results */}
                  {!hasMore && airports.length > 0 && totalPages > 0 && (
                    <div className="p-2 text-center text-xs text-jet-gray-500 bg-jet-gray-50">
                      <i className="fas fa-check-circle mr-1"></i>
                      Last page
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 text-center text-jet-gray-500">
                  {searchTerm ? (
                    <>
                      <i className="fas fa-search mb-2 text-2xl text-jet-gray-400"></i>
                      <p>No airports found for "{searchTerm}"</p>
                      <p className="text-xs mt-1">Try a different search term</p>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plane-slash mb-2 text-2xl text-jet-gray-400"></i>
                      <p>No airports available</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-xs mt-1 ml-1">{error}</p>
      )}
    </div>
  );
};

export default AirportSelect;